/**
 * GLASS CARD COMPONENT - Apple Liquid Glass Design System
 * macOS Tahoe 26.2 & iOS 26.2
 * Based on WWDC25: Meet Liquid Glass
 *
 * Features:
 * - Liquid Glass material with lensing
 * - Pearlescent shimmer effect on hover
 * - Ambient glow from accent color
 * - Specular highlight at top edge
 * - Adaptive hover states
 * - Multiple variants for different use cases
 */

import { HTMLAttributes, ReactNode, forwardRef } from 'react'
import { motion, HTMLMotionProps } from 'framer-motion'
import { cn } from '../../lib/utils'

type GlassCardVariant = 'regular' | 'clear' | 'thick' | 'stat'
type GlassCardSize = 'sm' | 'md' | 'lg'
type GlassCardAccent = 'none' | 'blue' | 'green' | 'purple' | 'orange' | 'pink' | 'teal' | 'red'

interface GlassCardProps extends Omit<HTMLMotionProps<'div'>, 'children'> {
  children: ReactNode
  variant?: GlassCardVariant
  size?: GlassCardSize
  accent?: GlassCardAccent
  hoverable?: boolean
  pressable?: boolean
  pearl?: boolean
  className?: string
}

const accentGradients: Record<GlassCardAccent, string> = {
  none: '',
  blue: 'liquid-stat-gradient-blue liquid-ambient-glow liquid-ambient-blue',
  green: 'liquid-stat-gradient-green liquid-ambient-glow liquid-ambient-green',
  purple: 'liquid-stat-gradient-purple liquid-ambient-glow liquid-ambient-purple',
  orange: 'liquid-stat-gradient-orange liquid-ambient-glow liquid-ambient-orange',
  pink: 'liquid-stat-gradient-pink liquid-ambient-glow liquid-ambient-pink',
  teal: 'liquid-stat-gradient-teal liquid-ambient-glow liquid-ambient-teal',
  red: 'liquid-ambient-glow liquid-ambient-red',
}

const sizeClasses: Record<GlassCardSize, string> = {
  sm: 'p-4 rounded-xl',
  md: 'p-6 rounded-2xl',
  lg: 'p-8 rounded-3xl',
}

const variantClasses: Record<GlassCardVariant, string> = {
  regular: 'liquid-material-regular',
  clear: 'liquid-material-clear',
  thick: 'liquid-material-thick',
  stat: 'liquid-glass-card',
}

export const GlassCard = forwardRef<HTMLDivElement, GlassCardProps>(({
  children,
  variant = 'regular',
  size = 'md',
  accent = 'none',
  hoverable = true,
  pressable = false,
  pearl = false,
  className,
  ...props
}, ref) => {
  return (
    <motion.div
      ref={ref}
      whileHover={hoverable ? {
        scale: 1.01,
        y: -2,
        transition: { duration: 0.2, ease: [0.23, 1, 0.32, 1] }
      } : undefined}
      whileTap={pressable ? {
        scale: 0.98,
        transition: { duration: 0.1, ease: [0.23, 1, 0.32, 1] }
      } : undefined}
      className={cn(
        // Base styles
        'relative overflow-hidden',
        variantClasses[variant],
        sizeClasses[size],

        // Accent gradient
        accentGradients[accent],

        // Pearlescent effect
        pearl && 'liquid-glass-card-pearl',

        // Hoverable cursor
        (hoverable || pressable) && 'cursor-pointer',

        // Transition
        'transition-shadow duration-200 ease-out',

        className
      )}
      {...props}
    >
      {/* Specular highlight */}
      <div
        className="absolute top-0 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/20 pointer-events-none"
        aria-hidden="true"
      />

      {/* Content */}
      <div className="relative z-[1]">
        {children}
      </div>
    </motion.div>
  )
})

GlassCard.displayName = 'GlassCard'

/**
 * StatCard - Specialized card for displaying statistics
 */
interface StatCardProps {
  title: string
  value: string | number
  subtitle?: string
  icon?: ReactNode
  trend?: {
    value: number
    direction: 'up' | 'down' | 'neutral'
  }
  accent?: GlassCardAccent
  onClick?: () => void
  className?: string
}

export function StatCard({
  title,
  value,
  subtitle,
  icon,
  trend,
  accent = 'blue',
  onClick,
  className,
}: StatCardProps) {
  return (
    <GlassCard
      variant="stat"
      size="md"
      accent={accent}
      hoverable={!!onClick}
      pressable={!!onClick}
      onClick={onClick}
      className={cn('group', className)}
    >
      {/* Header with icon and trend */}
      <div className="flex items-start justify-between mb-4">
        {/* Icon container */}
        {icon && (
          <div className={cn(
            'w-12 h-12 rounded-xl flex items-center justify-center',
            'bg-light-bg-tertiary/50 dark:bg-dark-bg-tertiary/50',
            'group-hover:bg-light-bg-tertiary dark:group-hover:bg-dark-bg-tertiary',
            'transition-colors duration-200'
          )}>
            {icon}
          </div>
        )}

        {/* Trend indicator */}
        {trend && (
          <div className={cn(
            'flex items-center gap-1 text-footnote font-semibold',
            trend.direction === 'up' && 'text-green dark:text-green-dark',
            trend.direction === 'down' && 'text-red dark:text-red-dark',
            trend.direction === 'neutral' && 'text-light-text-tertiary dark:text-dark-text-tertiary'
          )}>
            {trend.direction === 'up' && (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 17L17 7M17 7H7M17 7V17" />
              </svg>
            )}
            {trend.direction === 'down' && (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 7L7 17M7 17H17M7 17V7" />
              </svg>
            )}
            <span>{trend.value > 0 ? '+' : ''}{trend.value}%</span>
          </div>
        )}
      </div>

      {/* Value */}
      <div className="mb-1">
        <span className="text-title1 font-bold text-light-text-primary dark:text-dark-text-primary tracking-tight">
          {value}
        </span>
      </div>

      {/* Title */}
      <div className="text-subheadline font-medium text-light-text-secondary dark:text-dark-text-secondary">
        {title}
      </div>

      {/* Subtitle */}
      {subtitle && (
        <div className="mt-2 text-footnote text-light-text-tertiary dark:text-dark-text-tertiary">
          {subtitle}
        </div>
      )}
    </GlassCard>
  )
}

/**
 * ContentCard - Card for content sections (charts, tables, etc.)
 */
interface ContentCardProps extends Omit<GlassCardProps, 'variant'> {
  title?: string
  subtitle?: string
  action?: ReactNode
  noPadding?: boolean
}

export function ContentCard({
  title,
  subtitle,
  action,
  noPadding = false,
  children,
  className,
  ...props
}: ContentCardProps) {
  return (
    <GlassCard
      variant="regular"
      hoverable={false}
      className={cn(noPadding && 'p-0', className)}
      {...props}
    >
      {/* Header */}
      {(title || action) && (
        <div className={cn(
          'flex items-start justify-between',
          noPadding ? 'px-6 pt-6 pb-4' : 'mb-6'
        )}>
          <div>
            {title && (
              <h3 className="text-headline font-semibold text-light-text-primary dark:text-dark-text-primary tracking-tight">
                {title}
              </h3>
            )}
            {subtitle && (
              <p className="mt-1 text-subheadline text-light-text-secondary dark:text-dark-text-secondary">
                {subtitle}
              </p>
            )}
          </div>
          {action && (
            <div className="flex-shrink-0">
              {action}
            </div>
          )}
        </div>
      )}

      {/* Content */}
      <div className={noPadding ? '' : ''}>
        {children}
      </div>
    </GlassCard>
  )
}

/**
 * ActionCard - Card with primary action
 */
interface ActionCardProps {
  title: string
  description?: string
  icon?: ReactNode
  action: {
    label: string
    onClick: () => void
  }
  accent?: GlassCardAccent
  className?: string
}

export function ActionCard({
  title,
  description,
  icon,
  action,
  accent = 'blue',
  className,
}: ActionCardProps) {
  return (
    <GlassCard
      variant="regular"
      size="md"
      accent={accent}
      hoverable
      pressable
      onClick={action.onClick}
      className={cn('group', className)}
    >
      <div className="flex items-center gap-4">
        {/* Icon */}
        {icon && (
          <div className={cn(
            'w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0',
            'bg-light-bg-tertiary/50 dark:bg-dark-bg-tertiary/50',
            'group-hover:bg-light-bg-tertiary dark:group-hover:bg-dark-bg-tertiary',
            'transition-colors duration-200'
          )}>
            {icon}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3 className="text-headline font-semibold text-light-text-primary dark:text-dark-text-primary tracking-tight">
            {title}
          </h3>
          {description && (
            <p className="mt-0.5 text-subheadline text-light-text-secondary dark:text-dark-text-secondary line-clamp-2">
              {description}
            </p>
          )}
        </div>

        {/* Arrow */}
        <div className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center bg-light-bg-tertiary/50 dark:bg-dark-bg-tertiary/50 group-hover:bg-blue/10 dark:group-hover:bg-blue-dark/10 transition-colors duration-200">
          <svg
            className="w-5 h-5 text-light-text-tertiary dark:text-dark-text-tertiary group-hover:text-blue dark:group-hover:text-blue-dark transition-colors duration-200"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </GlassCard>
  )
}
